import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Observable, map, startWith} from 'rxjs';
import {PlaygroundService} from '../../service/playground.service';
import {PlaygroundSalesOrder, PlaygroundLine, PlaygroundPayment, PlaygroundAllocation, PlaygroundProductType, PlaygroundSyncQueue, PlaygroundLookups, PlaygroundClient, PlaygroundTravelerForm, PlaygroundTravelerRow} from '../../model/playground.model';
import {PlaygroundLineDialogComponent} from '../playground-line-dialog/playground-line-dialog.component';
import {PlaygroundPaymentDialogComponent} from '../playground-payment-dialog/playground-payment-dialog.component';
import {PlaygroundAllocationDialogComponent} from '../playground-allocation-dialog/playground-allocation-dialog.component';
import {PlaygroundTravelerDialogComponent} from '../playground-traveler-dialog/playground-traveler-dialog.component';

@Component({
  selector: 'playground-sales-order',
  templateUrl: './playground-sales-order.component.html',
  standalone: false
})
export class PlaygroundSalesOrderComponent implements OnInit {
  salesOrderId!: number;
  salesOrder: PlaygroundSalesOrder | null = null;
  headerForm!: FormGroup;
  productTypes: PlaygroundProductType[] = [];
  syncEntries: PlaygroundSyncQueue[] = [];
  lookups: PlaygroundLookups | null = null;
  clientControl = new FormControl('');
  filteredClients!: Observable<PlaygroundClient[]>;

  lineColumns = ['productType', 'serviceName', 'vendorName', 'cost', 'price', 'agentCommission', 'currencyCode', 'totalUnits', 'serviceStartDate', 'actions'];
  paymentColumns = ['travelerName', 'productType', 'amount', 'currencyCode', 'paymentDate', 'reference', 'actions'];
  allocationColumns = ['vendorName', 'productType', 'amount', 'currencyCode', 'actions'];
  travelerColumns = ['travelerName', 'email', 'phone', 'address', 'mainContact', 'actions'];
  syncColumns = ['id', 'changeType', 'changeAction', 'isSynced', 'isFailed', 'retryCount', 'dateCreated'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private playgroundService: PlaygroundService
  ) {}

  ngOnInit(): void {
    this.headerForm = this.fb.group({
      tripReference: [''],
      bookedDate: [''],
      tripStartDate: [''],
      numberOfDays: [null],
      mainClientId: [null],
      mainClientName: [''],
      mainClientEmail: [''],
      quoteAgentRefId: [null],
      isCancelled: [false]
    });

    this.filteredClients = this.clientControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterClients(value || ''))
    );

    this.playgroundService.getProductTypes().subscribe(types => this.productTypes = types);
    this.playgroundService.getLookups().subscribe(lookups => this.lookups = lookups);

    this.route.params.subscribe(params => {
      this.salesOrderId = +params['id'];
      this.loadSalesOrder();
    });
  }

  private _filterClients(value: string): PlaygroundClient[] {
    if (!this.lookups) return [];
    const filter = value.toLowerCase();
    return this.lookups.clients.filter(c =>
      c.name?.toLowerCase().includes(filter) || c.email?.toLowerCase().includes(filter));
  }

  displayClient(client: PlaygroundClient | string): string {
    if (!client) return '';
    return typeof client === 'string' ? client : client.name;
  }

  onClientSelected(client: PlaygroundClient): void {
    this.headerForm.patchValue({
      mainClientId: client.clientId,
      mainClientName: client.name,
      mainClientEmail: client.email
    });
  }

  loadSalesOrder(): void {
    this.playgroundService.getSalesOrder(this.salesOrderId).subscribe(order => {
      this.salesOrder = order;
      this.headerForm.patchValue({
        tripReference: order.tripReference,
        bookedDate: order.bookedDate ? new Date(order.bookedDate) : null,
        tripStartDate: order.tripStartDate ? new Date(order.tripStartDate) : null,
        numberOfDays: order.numberOfDays,
        mainClientId: order.mainClientId,
        mainClientName: order.mainClientName,
        mainClientEmail: order.mainClientEmail,
        quoteAgentRefId: order.quoteAgentRefId,
        isCancelled: order.isCancelled
      });
      if (order.mainClientName) {
        this.clientControl.setValue(order.mainClientName);
      }
      this.headerForm.markAsPristine();
    });
  }

  saveHeader(): void {
    const vals = this.headerForm.value;
    this.playgroundService.updateSalesOrder({
      salesOrderId: this.salesOrderId,
      tripReference: vals.tripReference,
      bookedDate: vals.bookedDate instanceof Date ? vals.bookedDate.toISOString().substring(0, 10) : vals.bookedDate || null,
      tripStartDate: vals.tripStartDate instanceof Date ? vals.tripStartDate.toISOString().substring(0, 10) : vals.tripStartDate || null,
      numberOfDays: vals.numberOfDays,
      mainClientId: vals.mainClientId,
      mainClientName: vals.mainClientName,
      mainClientEmail: vals.mainClientEmail,
      quoteAgentRefId: vals.quoteAgentRefId,
      isCancelled: vals.isCancelled
    }).subscribe(() => {
      this.snackBar.open('Header saved', 'OK', {duration: 2000});
      this.loadSalesOrder();
    });
  }

  get serviceLines(): PlaygroundLine[] {
    return (this.salesOrder?.salesOrderLines || []).filter(l => l.serviceId !== 9);
  }

  getAgentCommission(line: PlaygroundLine): number | null {
    const commission = (this.salesOrder?.salesOrderLines || [])
      .find(l => l.serviceId === 9 && l.itineraryServiceId === line.id);
    return commission?.actualCost ?? null;
  }

  getProductTypeName(productTypeId: number | undefined): string {
    if (!productTypeId) return '';
    const pt = this.productTypes.find(t => t.id === productTypeId);
    return pt ? pt.name : String(productTypeId);
  }

  triggerSync(): void {
    this.playgroundService.triggerSync(this.salesOrderId).subscribe(entry => {
      this.snackBar.open(`Sync entry created: ${entry.changeType}/${entry.changeAction}. Sending to ERP...`, 'OK', {duration: 3000});
      this.playgroundService.sendToErpQueue(this.salesOrderId).subscribe({
        next: () => {
          this.snackBar.open('Sent to ERP queue successfully', 'OK', {duration: 3000});
          this.loadSalesOrder();
        },
        error: (err) => {
          this.snackBar.open('Sync entry created but ERP send failed: ' + (err?.message || 'Unknown error'), 'Close', {duration: 5000});
          this.loadSalesOrder();
        }
      });
    });
  }

  startTrip(): void {
    this.playgroundService.startTrip(this.salesOrderId).subscribe(() => {
      this.snackBar.open('Trip started. Sending to ERP...', 'OK', {duration: 3000});
      this.playgroundService.sendToErpQueue(this.salesOrderId).subscribe({
        next: () => {
          this.snackBar.open('Trip started + sent to ERP queue', 'OK', {duration: 3000});
          this.loadSalesOrder();
        },
        error: (err) => {
          this.snackBar.open('Trip started but ERP send failed: ' + (err?.message || 'Unknown error'), 'Close', {duration: 5000});
          this.loadSalesOrder();
        }
      });
    });
  }

  markFam(): void {
    const next = !this.salesOrder?.isFamTrip;
    this.playgroundService.markFamTrip(this.salesOrderId, next).subscribe(() => {
      this.snackBar.open(next ? 'Marked as FAM trip' : 'FAM trip flag cleared', 'OK', {duration: 2000});
      this.loadSalesOrder();
    });
  }

  addTraveler(): void {
    const dialogRef = this.dialog.open(PlaygroundTravelerDialogComponent, {width: '760px'});
    dialogRef.componentInstance.salesOrderId = this.salesOrderId;
    dialogRef.componentInstance.suggestedTravelerId = 9_000_000 + this.salesOrderId * 100 + 1;
    dialogRef.afterClosed().subscribe((result: PlaygroundTravelerForm | undefined) => {
      if (result) {
        this.playgroundService.createTraveler(result).subscribe({
          next: () => {
            this.snackBar.open('Traveler added', 'OK', {duration: 2000});
            this.loadSalesOrder();
          },
          error: err => this.snackBar.open(
            'Failed to add traveler: ' + (err?.error?.detail || err?.message || 'Unknown'),
            'Close', {duration: 6000})
        });
      }
    });
  }

  get travelers(): PlaygroundTravelerRow[] {
    return (this.salesOrder?.salesOrderTravelers || []).filter(t => !t.isTravelerDeleted);
  }

  travelerAddressLine(t: PlaygroundTravelerRow): string {
    const parts = [t.address1, t.address2, t.city, t.state, t.country, t.postalCode].filter(p => !!p);
    return parts.join(', ');
  }

  deleteTraveler(t: PlaygroundTravelerRow): void {
    if (!t.id) {
      return;
    }
    this.playgroundService.deleteTraveler(t.id).subscribe(() => {
      this.snackBar.open('Traveler removed', 'OK', {duration: 2000});
      this.loadSalesOrder();
    });
  }

  cancelTrip(): void {
    this.playgroundService.cancelTrip(this.salesOrderId).subscribe(() => {
      this.snackBar.open('Trip cancelled. Sending to ERP...', 'OK', {duration: 3000});
      this.playgroundService.sendToErpQueue(this.salesOrderId).subscribe({
        next: () => {
          this.snackBar.open('Trip cancelled + sent to ERP queue', 'OK', {duration: 3000});
          this.loadSalesOrder();
        },
        error: (err) => {
          this.snackBar.open('Trip cancelled but ERP send failed: ' + (err?.message || 'Unknown error'), 'Close', {duration: 5000});
          this.loadSalesOrder();
        }
      });
    });
  }

  addLine(): void {
    const dialogRef = this.dialog.open(PlaygroundLineDialogComponent, {width: '700px'});
    dialogRef.componentInstance.salesOrderId = this.salesOrderId;
    dialogRef.componentInstance.productTypes = this.productTypes;
    dialogRef.componentInstance.lookups = this.lookups;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.playgroundService.createLine(result).subscribe(() => this.loadSalesOrder());
      }
    });
  }

  editLine(line: PlaygroundLine): void {
    const dialogRef = this.dialog.open(PlaygroundLineDialogComponent, {width: '700px'});
    dialogRef.componentInstance.salesOrderId = this.salesOrderId;
    dialogRef.componentInstance.productTypes = this.productTypes;
    dialogRef.componentInstance.lookups = this.lookups;
    dialogRef.componentInstance.editData = line;
    dialogRef.componentInstance.agentCommissionAmount = this.getAgentCommission(line);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.playgroundService.updateLine({...result, salesOrderLineId: line.id}).subscribe(() => this.loadSalesOrder());
      }
    });
  }

  deleteLine(line: PlaygroundLine): void {
    this.playgroundService.deleteLine(line.id!).subscribe(() => this.loadSalesOrder());
  }

  addPayment(): void {
    const dialogRef = this.dialog.open(PlaygroundPaymentDialogComponent, {width: '600px'});
    dialogRef.componentInstance.salesOrderId = this.salesOrderId;
    dialogRef.componentInstance.productTypes = this.productTypes;
    dialogRef.componentInstance.lookups = this.lookups;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.playgroundService.createPayment(result).subscribe(() => this.loadSalesOrder());
      }
    });
  }

  deletePayment(payment: PlaygroundPayment): void {
    this.playgroundService.deletePayment(payment.id!).subscribe(() => this.loadSalesOrder());
  }

  addAllocation(): void {
    const dialogRef = this.dialog.open(PlaygroundAllocationDialogComponent, {width: '600px'});
    dialogRef.componentInstance.salesOrderId = this.salesOrderId;
    dialogRef.componentInstance.productTypes = this.productTypes;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.playgroundService.createAllocation(result).subscribe(() => this.loadSalesOrder());
      }
    });
  }

  deleteAllocation(allocation: PlaygroundAllocation): void {
    this.playgroundService.deleteAllocation(allocation.id!).subscribe(() => this.loadSalesOrder());
  }

  goBack(): void {
    this.router.navigate(['/Playground']);
  }
}
